import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Plus, Wand2, BookOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface WritingSession {
  id: string;
  created_at: string;
  source_name: string;
  content: string;
  duration_minutes: number;
  energy_quality: string;
  key_insights: string;
}

export default function AutomaticWriting() {
  const [sessions, setSessions] = useState<WritingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [isWriting, setIsWriting] = useState(false);
  const [showHistory, setShowHistory] = useState(true);
  const { toast } = useToast();

  const [sessionData, setSessionData] = useState({
    source_name: '',
    content: '',
    duration_minutes: 0,
    energy_quality: '',
    key_insights: ''
  });

  const [startTime, setStartTime] = useState<Date | null>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('automatic_writing_sessions')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast({ title: 'Error loading sessions', variant: 'destructive' });
    } else {
      setSessions(data || []);
    }
    setLoading(false);
  };

  const startWriting = () => {
    setIsWriting(true);
    setShowHistory(false);
    setStartTime(new Date());
    setSessionData({
      source_name: '',
      content: '',
      duration_minutes: 0,
      energy_quality: '',
      key_insights: ''
    });
  };

  const handleSave = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const duration = startTime ? Math.round((new Date().getTime() - startTime.getTime()) / 60000) : 0;

    const { error } = await supabase
      .from('automatic_writing_sessions')
      .insert({
        user_id: user.id,
        ...sessionData,
        duration_minutes: duration
      });

    if (error) {
      toast({ title: 'Error saving session', variant: 'destructive' });
    } else {
      toast({ title: 'Session saved' });
      setIsWriting(false);
      setShowHistory(true);
      loadSessions();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading automatic writing space...</div>;
  }

  if (isWriting) {
    return (
      <div className="space-y-6">
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Wand2 className="h-5 w-5 text-primary animate-pulse" />
                Automatic Writing Session
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => { setIsWriting(false); setShowHistory(true); }}>
                Cancel
              </Button>
            </div>
            <CardDescription>
              Relax, quiet your mind, and let the words flow through you without judgment or editing.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Source or Guide (optional)</Label>
              <Input
                value={sessionData.source_name}
                onChange={(e) => setSessionData({ ...sessionData, source_name: e.target.value })}
                placeholder="Higher self, guide name, or leave blank"
                className="text-lg"
              />
            </div>

            <div className="space-y-2">
              <Label>Write freely - let it flow</Label>
              <Textarea
                value={sessionData.content}
                onChange={(e) => setSessionData({ ...sessionData, content: e.target.value })}
                placeholder="Begin writing... don't think, just let the words come..."
                rows={18}
                className="text-base leading-relaxed resize-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Energy Quality</Label>
                <Input
                  value={sessionData.energy_quality}
                  onChange={(e) => setSessionData({ ...sessionData, energy_quality: e.target.value })}
                  placeholder="Peaceful, elevated, intense..."
                />
              </div>
              <div className="space-y-2">
                <Label>Key Insights</Label>
                <Input
                  value={sessionData.key_insights}
                  onChange={(e) => setSessionData({ ...sessionData, key_insights: e.target.value })}
                  placeholder="Main messages or themes"
                />
              </div>
            </div>

            <Button onClick={handleSave} className="w-full" size="lg">
              Save Session
            </Button>
          </CardContent>
        </Card>

        <Card className="bg-primary/10 border-primary/20">
          <CardContent className="pt-6">
            <h4 className="font-light text-primary mb-3">✨ Tips for Automatic Writing</h4>
            <ul className="text-sm space-y-2 text-muted-foreground font-light">
              <li>• Set an intention: "I invite my highest guides to communicate through me"</li>
              <li>• Don't overthink - write whatever comes, even if it seems random</li>
              <li>• Keep your hand moving - don't pause to edit or judge</li>
              <li>• Notice if the writing feels different from your usual voice</li>
              <li>• Date and time can affect quality - experiment with morning vs evening</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary animate-pulse" />
            Automatic Writing Space
          </CardTitle>
          <CardDescription>
            Automatic writing is channeling guidance by allowing spirit, guides, or your higher self to write through you. Enter a meditative state and let wisdom flow onto the page without conscious control.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={startWriting} size="lg" className="w-full mb-6">
            <Plus className="h-5 w-5 mr-2" />
            Begin New Writing Session
          </Button>

          {showHistory && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <BookOpen className="h-4 w-4 text-primary" />
                <h3 className="font-light">Previous Sessions</h3>
              </div>

              {sessions.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No sessions yet. Begin your first automatic writing session above.
                </p>
              ) : (
                sessions.map((session) => (
                  <Card key={session.id} className="bg-muted/30 animate-fade-in">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          {session.source_name && (
                            <CardTitle className="text-lg">{session.source_name}</CardTitle>
                          )}
                          <div className="flex gap-2">
                            {session.duration_minutes > 0 && (
                              <Badge variant="outline">{session.duration_minutes} min</Badge>
                            )}
                            {session.energy_quality && (
                              <Badge variant="secondary">{session.energy_quality}</Badge>
                            )}
                          </div>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(session.created_at), 'PPP')}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed font-light">{session.content}</p>
                      </div>
                      {session.key_insights && (
                        <div className="pt-3 border-t border-border/50">
                          <p className="text-sm font-light text-primary mb-1">Key Insights:</p>
                          <p className="text-sm text-muted-foreground font-light italic">{session.key_insights}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
